/**
 * 글로벌 서비스를 위한 Timezone 유틸리티
 * 
 * 원칙:
 * - 서버/DB는 항상 UTC로 통신
 * - 프론트엔드는 사용자의 timezone으로 표시
 * 
 * 주요 함수:
 * - formatDate: 날짜만 (2024.01.14)
 * - formatTime: 시간만 (14:30:00)
 * - formatDateTime: 날짜+시간 (2024-01-14 14:30:00)
 * 만기 D-day 표시는 API `cal_due_day_num` + 동일 모듈의 `dueDateDisplay` (`formatDueDateLine`) 사용
 * 
 * Import:
 * import { formatDate, formatTime, formatDateTime } from '@/utils/date';
 */

import { format, parseISO } from 'date-fns';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';

/**
 * 브라우저의 현재 timezone 가져오기
 * @returns IANA timezone 문자열 (예: "Asia/Seoul", "America/New_York")
 */
export function getBrowserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * UTC Date를 사용자의 timezone으로 변환
 * @param utcDate UTC Date 객체 또는 ISO 문자열
 * @param timezone IANA timezone (기본값: 브라우저 timezone)
 * @returns timezone이 적용된 Date 객체
 */
export function utcToUserTimezone(
  utcDate: Date | string,
  timezone?: string
): Date {
  const tz = timezone || getBrowserTimezone();
  const date = typeof utcDate === 'string' ? parseServerDate(utcDate) : utcDate;
  return toZonedTime(date, tz);
}

/**
 * 서버/DB 문자열을 Date로 파싱한다.
 *
 * 원칙: 서버/DB는 항상 UTC로 통신한다. 따라서 "시간 성분이 있으나 타임존 표기가
 * 없는" 문자열(예: "2024-01-14 10:30:00", "2024-01-14T10:30:00")은 UTC로 간주해
 * Z를 붙여 파싱한다. 날짜만 있는 문자열("2024-01-14")은 기존대로 로컬 자정으로 둔다.
 */
function parseServerDate(value: string): Date {
  const s = value.trim();
  const hasTime = /\d{2}:\d{2}/.test(s);
  const hasTz = /([zZ]|[+-]\d{2}:?\d{2})$/.test(s);
  if (hasTime && !hasTz) {
    return parseISO(`${s.replace(' ', 'T')}Z`);
  }
  return parseISO(s);
}

/**
 * 사용자 timezone의 Date를 UTC로 변환
 * @param localDate 로컬 Date 객체
 * @param timezone IANA timezone (기본값: 브라우저 timezone)
 * @returns UTC Date 객체
 */
function userTimezoneToUtc(
  localDate: Date,
  timezone?: string
): Date {
  const tz = timezone || getBrowserTimezone();
  return fromZonedTime(localDate, tz);
}

/**
 * UTC Date를 사용자 timezone의 지정된 포맷으로 변환
 * @param utcDate UTC Date 객체 또는 ISO 문자열
 * @param formatString date-fns 포맷 문자열 (기본값: "yyyy-MM-dd HH:mm:ss")
 * @param timezone IANA timezone (기본값: 브라우저 timezone)
 * @returns 포맷된 날짜 문자열
 * 
 * @example
 * formatToUserTimezone(utcDate, "yyyy.MM.dd") // "2024.01.14"
 * formatToUserTimezone(utcDate, "yyyy-MM-dd HH:mm:ss") // "2024-01-14 10:30:00"
 */
function formatToUserTimezone(
  utcDate: Date | string | null | undefined,
  formatString: string = 'yyyy-MM-dd HH:mm:ss',
  timezone?: string
): string {
  if (!utcDate) return '-';
  
  try {
    const zonedDate = utcToUserTimezone(utcDate, timezone);
    return format(zonedDate, formatString);
  } catch (error) {
    console.error('Error formatting date:', error);
    return '-';
  }
}

/**
 * 날짜만 표시 (YYYY.MM.DD)
 * @param date UTC Date 객체, ISO 문자열, 또는 밀리초 타임스탬프
 * @returns "2024.01.14" 형식
 */
export function formatDate(
  date: Date | string | number | null | undefined
): string {
  if (date == null) return '-';
  if (typeof date === 'number') {
    return formatToUserTimezone(new Date(date), 'yyyy.MM.dd');
  }
  return formatToUserTimezone(date, 'yyyy.MM.dd');
}

/**
 * 시간만 표시 (HH:mm:ss)
 * @param date UTC Date 객체 또는 ISO 문자열
 * @returns "14:30:00" 형식
 */
export function formatTime(
  date: Date | string | null | undefined
): string {
  return formatToUserTimezone(date, 'HH:mm:ss');
}

/**
 * 날짜+시간 표시 (YYYY-MM-DD HH:mm:ss)
 * @param date UTC Date 객체 또는 ISO 문자열
 * @returns "2024-01-14 14:30:00" 형식
 */
export function formatDateTime(
  date: Date | string | null | undefined
): string {
  return formatToUserTimezone(date, 'yyyy-MM-dd HH:mm:ss');
}

/**
 * 날짜+시간 표시 (YYYY.MM.DD HH:mm)
 * @param date UTC Date 객체 또는 ISO 문자열
 * @returns "2024.01.14 14:30" 형식
 */
export function formatDateTimeShort(
  date: Date | string | null | undefined
): string {
  return formatToUserTimezone(date, 'yyyy.MM.dd HH:mm');
}

/**
 * 현재 사용자의 timezone offset 가져오기
 * @returns "+09:00", "-05:00" 형식의 문자열
 */
function getTimezoneOffset(): string {
  const offset = -new Date().getTimezoneOffset();
  const hours = Math.floor(Math.abs(offset) / 60);
  const minutes = Math.abs(offset) % 60;
  const sign = offset >= 0 ? '+' : '-';
  
  return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/**
 * Timezone 정보를 포함한 현재 시간 정보
 */
export interface TimezoneInfo {
  /** IANA timezone (예: "Asia/Seoul") */
  timezone: string;
  /** Timezone offset (예: "+09:00") */
  offset: string;
  /** 현재 로컬 시간 */
  localTime: Date;
  /** 현재 UTC 시간 */
  utcTime: Date;
}

/**
 * 현재 사용자의 timezone 정보 가져오기
 * @returns TimezoneInfo 객체
 */
export function getTimezoneInfo(): TimezoneInfo {
  const timezone = getBrowserTimezone();
  const localTime = new Date();
  const utcTime = userTimezoneToUtc(localTime);
  const offset = getTimezoneOffset();
  
  return {
    timezone,
    offset,
    localTime,
    utcTime,
  };
}
